import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { type ReactElement } from 'react';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { CheckboxPopover } from '@/ui/composites/filters/CheckboxPopover';
import { DropdownFiltersPopover } from '@/ui/composites/filters/DropdownFiltersPopover';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { Box, Cancel, IconButton, Text, Tooltip, useTheme } from '@/ui/primitives';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from './tableConstants';
import { TableDatePopover } from './TableDatePopover';
import { TableSortPopover } from './TableSortPopover';
import { buildToolbarIconButtonSx, buildToolbarIconCircleSx } from './tableToolbar.helpers';
import { type TableToolbarProps } from './tableToolbar.types';
import { useTableToolbarState } from './useTableToolbarState';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: '15px',
      lineHeight: 1.4,
    },
  },
} as const;

export const TableToolbar = <TSortKey extends string, TStatus extends string>({
  title,
  searchAriaLabel,
  onDebouncedSearchChange,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
  columnOptions,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  defaultSortKey,
  defaultSortDirection,
  sortOptions,
  onSortChange,
  filterStatuses,
  statusOptions,
  onFilterStatusesChange,
  filterSuites = [],
  onFilterSuitesChange,
  suiteOptions,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  previewFromDate = null,
  previewToDate = null,
  onFromDateChange,
  onToDateChange,
  availableTags,
  includeLatestQuickFilter = false,
  statusFilterLabel = 'Latest Status',
  summaryCount = null,
  summarySingularLabel,
  summaryPluralLabel,
}: TableToolbarProps<TSortKey, TStatus>): ReactElement => {
  const mandatoryColumnVisibilityModel = columnOptions.reduce<Record<string, true>>(
    (acc, option) => {
      if (option.mandatory) {
        acc[option.field] = true;
      }
      return acc;
    },
    {},
  );
  const theme = useTheme();
  const toolbarControlSize = 32;
  const toolbarExpandedSearchWidth = 252;
  const toolbarSearchWidthPx = `${toolbarExpandedSearchWidth}px`;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const {
    popoverState,
    searchExpanded,
    searchValue,
    hoverTarget,
    searchInputRef,
    hasNonDefaultColumns,
    hasActiveFilters,
    hasActiveDateFilters,
    hasActiveSort,
    hasActiveToolbarState,
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    setHoverTarget,
    setSearchValue,
    openSearch,
    handleSearchBlur,
    handleSearchKeyDown,
    openPopover,
    closePopover,
    preserveWindowScroll,
    resetToolbarFilters,
  } = useTableToolbarState({
    onDebouncedSearchChange,
    columnVisibilityModel,
    defaultColumnVisibilityModel,
    columnOptions,
    onColumnVisibilityModelChange,
    sortKey,
    sortDirection,
    defaultSortKey,
    defaultSortDirection,
    onSortChange,
    filterStatuses,
    onFilterStatusesChange,
    filterSuites,
    onFilterSuitesChange,
    filterTags,
    onFilterTagsChange,
    dateQuickFilter,
    onDateQuickFilterChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    summaryCount,
    summarySingularLabel,
    summaryPluralLabel,
    previewFromDate,
    previewToDate,
  });
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const toolbarIconCircleSx = buildToolbarIconCircleSx;
  const toolbarIconTooltipDelayMs = 250;
  return (
    <Stack
      direction="row"
      justifyContent={title ? 'space-between' : 'flex-end'}
      alignItems="center"
      gap="md"
      sx={{
        width: '100%',
        minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
        height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
      }}
    >
      {title ? (
        <Box
          sx={{
            minHeight: `${toolbarControlSize}px`,
            display: 'flex',
            alignItems: 'center',
            pl: s('sm'),
          }}
        >
          <Text
            role="bodyStrong"
            sx={{
              fontSize: (muiTheme) => muiTheme.typography.h6.fontSize,
              lineHeight: (muiTheme) => muiTheme.typography.h6.lineHeight,
            }}
          >
            {title}
          </Text>
        </Box>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        gap="sm"
        sx={{
          minWidth: 0,
          height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          boxSizing: 'border-box',
          justifyContent: 'center',
        }}
      >
        {dateRangeLabel ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pr: s('sm'),
            }}
          >
            <Text
              role="bodyMuted"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
              }}
            >
              {dateRangeLabel}
            </Text>
          </Box>
        ) : null}
        <Tooltip
          title="Date Filters"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open date filters"
            onClick={openPopover('date')}
            onMouseEnter={() => {
              setHoverTarget('date');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveDateFilters ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'date')}>
              <EventOutlinedIcon
                sx={{
                  color: hasActiveDateFilters ? 'primary.main' : toolbarInactiveIconColor,
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Columns"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open columns"
            onClick={openPopover('columns')}
            onMouseEnter={() => {
              setHoverTarget('columns');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'columns')}>
              <ViewColumnOutlinedIcon
                sx={{
                  color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Filters"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open filters"
            onClick={openPopover('filters')}
            onMouseEnter={() => {
              setHoverTarget('filters');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveFilters ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'filters')}>
              <FilterListOutlinedIcon
                sx={{
                  color: hasActiveFilters ? 'primary.main' : toolbarInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Sort"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open sort"
            onClick={openPopover('sort')}
            onMouseEnter={() => {
              setHoverTarget('sort');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'sort')}>
              <SortOutlinedIcon
                sx={{
                  color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            width: searchExpanded ? toolbarExpandedSearchWidth : 32,
            height: toolbarControlSize,
            minHeight: toolbarControlSize,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: (t) =>
              t.transitions.create('width', {
                duration: t.transitions.duration.shorter,
                easing: t.transitions.easing.easeInOut,
              }),
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              width: '100%',
              height: `${toolbarControlSize}px`,
              transform: searchExpanded
                ? 'translateY(-50%) translateX(0)'
                : 'translateY(-50%) translateX(-8px)',
              opacity: searchExpanded ? 1 : 0,
              pointerEvents: searchExpanded ? 'auto' : 'none',
              display: 'flex',
              alignItems: 'center',
              transition: (t) =>
                t.transitions.create(['opacity', 'transform'], {
                  duration: t.transitions.duration.shorter,
                  easing: t.transitions.easing.easeInOut,
                }),
            }}
          >
            <SearchFilterField
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
              placeholder="Search..."
              inputAriaLabel={searchAriaLabel}
              controlHeight={32}
              compact
              leadingSearchAdornment
              trailingSearchAdornment={false}
              filledClearIcon={false}
              sx={{
                width: toolbarSearchWidthPx,
                minWidth: toolbarSearchWidthPx,
                maxWidth: toolbarSearchWidthPx,
                '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                  height: `${toolbarControlSize}px !important`,
                },
                '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
                  height: `${toolbarControlSize}px !important`,
                  paddingLeft: `${s('xs')} !important`,
                  paddingInlineStart: `${s('xs')} !important`,
                },
              }}
              slotProps={{
                htmlInput: {
                  ref: (instance: HTMLInputElement | null) => {
                    searchInputRef.current = instance;
                  },
                  onBlur: handleSearchBlur,
                  onKeyDown: handleSearchKeyDown,
                },
              }}
            />
          </Box>
          <Box
            sx={{
              opacity: searchExpanded ? 0 : 1,
              pointerEvents: searchExpanded ? 'none' : 'auto',
              transform: searchExpanded ? 'scale(0.9)' : 'scale(1)',
              transition: (t) =>
                t.transitions.create(['opacity', 'transform'], {
                  duration: t.transitions.duration.shorter,
                  easing: t.transitions.easing.easeInOut,
                }),
            }}
          >
            <Tooltip
              title="Search"
              arrow
              enterDelay={toolbarIconTooltipDelayMs}
              slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            >
              <IconButton
                size="medium"
                aria-label="Open search"
                onClick={openSearch}
                sx={[
                  toolbarIconButtonSx,
                  {
                    width: toolbarControlSize,
                    height: toolbarControlSize,
                    minWidth: toolbarControlSize,
                  },
                ]}
              >
                <Box sx={toolbarIconCircleSx(hoverTarget === 'search')}>
                  <SearchOutlinedIcon
                    sx={{
                      color: toolbarInactiveIconColor,
                      width: '22px !important',
                      height: '22px !important',
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Tooltip
          title="Reset"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
          >
            <IconButton
              size="medium"
              aria-label="Reset filters"
              onClick={resetToolbarFilters}
              disabled={!hasActiveToolbarState}
              sx={[toolbarIconButtonSx, hasActiveToolbarState ? { color: 'primary.main' } : {}]}
            >
              <Box sx={toolbarIconCircleSx(hoverTarget === 'reset' && hasActiveToolbarState)}>
                <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
              </Box>
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
      <CheckboxPopover
        open={popoverState.type === 'columns'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        heading="Columns"
        headingIcon={
          <ViewColumnOutlinedIcon sx={{ width: '18px !important', height: '18px !important' }} />
        }
        minWidth={300}
        sections={[
          {
            key: 'columns',
            options: columnOptions.map((option) => {
              const checked = option.mandatory ? true : columnVisibilityModel[option.field];
              return {
                key: option.field,
                label: option.label,
                checked,
                disabled: Boolean(option.mandatory),
                onChange: (nextChecked: boolean) => {
                  if (option.mandatory) return;
                  preserveWindowScroll(() => {
                    onColumnVisibilityModelChange({
                      ...columnVisibilityModel,
                      [option.field]: nextChecked,
                      ...mandatoryColumnVisibilityModel,
                    });
                  });
                },
              };
            }),
          },
        ]}
      />
      <DropdownFiltersPopover
        open={popoverState.type === 'filters'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        heading="Filters"
        headingIcon={
          <FilterListOutlinedIcon sx={{ width: '18px !important', height: '18px !important' }} />
        }
        selectedStatuses={filterStatuses}
        onStatusesChange={(next) => {
          preserveWindowScroll(() => {
            onFilterStatusesChange(next as TStatus[]);
          });
        }}
        statusOptions={statusOptions}
        statusLabel={statusFilterLabel}
        selectedSuites={filterSuites}
        onSuitesChange={
          onFilterSuitesChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterSuitesChange(next);
                });
              }
            : undefined
        }
        suiteOptions={suiteOptions}
        selectedTags={filterTags}
        onTagsChange={(next) => {
          preserveWindowScroll(() => {
            onFilterTagsChange(next);
          });
        }}
        tagOptions={availableTags.map((tag) => ({ value: tag, label: tag }))}
      />
      <TableDatePopover
        open={popoverState.type === 'date'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        dateQuickFilter={dateQuickFilter}
        onDateQuickFilterChange={(next) => {
          preserveWindowScroll(() => {
            onDateQuickFilterChange(next);
          });
        }}
        fromDate={fromDate}
        toDate={toDate}
        previewFromDate={displayedFromDate}
        previewToDate={displayedToDate}
        onFromDateChange={(next) => {
          preserveWindowScroll(() => {
            onFromDateChange(next);
          });
        }}
        onToDateChange={(next) => {
          preserveWindowScroll(() => {
            onToDateChange(next);
          });
        }}
        includeLatestQuickFilter={includeLatestQuickFilter}
      />
      <TableSortPopover
        open={popoverState.type === 'sort'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        sortKey={sortKey}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        preserveWindowScroll={preserveWindowScroll}
      />
    </Stack>
  );
};

export type { CloneDateQuickFilter };
